<template>
  <dialog
    ref="dialogEl"
    class="search-dialog"
    :inert="!modelValue"
    :class="{ open: modelValue }"
    @cancel.prevent="close"
    @click.self="close"
  >
    <div class="search-dialog__top mb-12">
      <div class="flex align-center justify-end mb-xxs">
        <button
          class="search-dialog__close flex flex-nowrap align-center flex-gap-8"
          type="button"
          aria-label="Sluit zoekvenster"
          @click="close"
        >
          <span
            class="inline-flex mr-xxs"
            aria-hidden="true"
          >
            Sluiten
          </span>
          <UIIconComponent
            icon="close"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
    <div class="search-dialog__content">
      <div class="search-results-input ptb-md plr-lg sm-p-md">
        <UISearchInput
          ref="searchInputRef"
          v-model="search"
        />
      </div>
      <div class="search-results-wrapper">
        <div class="search-results-scroll">
          <div
            v-if="search"
            class="search-items"
          >
            <UISearchResults
              :search-string="search"
              :show-faq-questions="false"
            />
          </div>
        </div>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
  const props = defineProps<{ modelValue?: boolean }>()
  const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

  const dialogEl = ref<HTMLDialogElement | null>(null)
  const search = ref('')
  const searchInputRef = ref<any>(null)

  function close() {
    emit('update:modelValue', false)
  }

  let stopWatch: (() => void) | null = null

  onMounted(() => {
    stopWatch = watch(
      () => props.modelValue,
      (value) => {
        if (!dialogEl.value) return
        if (value) {
          dialogEl.value.showModal()
          document.documentElement.classList.add('overlay-open')
          nextTick(() => setTimeout(() => searchInputRef.value?.focus(), 50))
        } else {
          dialogEl.value.close()
          search.value = ''
          document.documentElement.classList.remove('overlay-open')
        }
      },
    )
  })

  onUnmounted(() => {
    if (stopWatch) stopWatch()
  })
</script>

<style scoped lang="scss">
  .search-dialog {
    border: 0;
    z-index: 500;
    padding: 0;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    background: transparent;
    width: min(900px, calc(100vw - 2rem));
    margin: 10vh auto 5vh auto;
    overflow: hidden;
    transition: all 0.6s var(--ease-fast-in-slow-out);
    transform: scale(0.9) translateY(2rem);

    &::backdrop {
      opacity: 0;
      background: rgba(0, 0, 0, 0.6);
      transition: all 0.4s var(--ease-fast-in-slow-out);
      transition-delay: 0.1s;
    }

    &.open {
      transform: scale(1) translateY(0);
      opacity: 1;
      visibility: visible;
      pointer-events: auto;

      &::backdrop {
        opacity: 1;
      }
    }
  }

  .search-dialog__top {
    color: $white;

    .flex {
      cursor: pointer;
    }
  }

  .search-dialog__content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    background-color: #fff;
    border-radius: 1.6rem;
    overflow: hidden;
  }

  .search-dialog__close {
    border: 0;
    background: transparent;
    cursor: pointer;
    font-size: 2rem;
    font-weight: 400;

    .icon {
      width: 2.4rem;
      height: 2.4rem;
      font-size: 2.4rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .search-results-input {
    background-color: $white;
    color: $black;
  }

  .search-results-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    background: #fff;

    &.is-scrollable-top::before,
    &.is-scrollable-bottom::after {
      content: '';
      display: block;
      position: absolute;
      z-index: 4;
      left: 0;
      width: 100%;
      height: 12rem;
      pointer-events: none;
    }

    &.is-scrollable-top::before {
      top: 0;
      background: linear-gradient(0deg, rgba($beige, 0) 0%, $beige 100%);
    }

    &.is-scrollable-bottom::after {
      bottom: 0;
      background: linear-gradient(180deg, rgba($beige, 0) 0%, $beige 100%);
    }
  }

  .search-results-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;

    .search-items {
      position: relative;
    }
  }
</style>
